import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Milk, Sparkles, Truck } from "lucide-react";
import { Reveal, SectionBadge } from "./Shared";
import { JUICES, SHOTS } from "../data";

const ALL = [...JUICES, ...SHOTS];

const NOTES = [
  { icon: Clock, title: "Order by 3pm", text: "for next-day pickup or delivery" },
  { icon: Truck, title: "$5 delivery fee", text: "or swing by for free pickup" },
  { icon: Milk, title: "1.5L & 1 gallon", text: "bulk sizes available on request" },
  { icon: Sparkles, title: "Custom blends", text: "made your way with advance notice" },
];

export default function DrinksMenu() {
  const [active, setActive] = useState(0);

  const renderGroup = (title, sub, items, offset) => (
    <div key={title} className={offset > 0 ? "mt-16" : ""}>
      <Reveal>
        <div className="mb-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
          <h3 className="font-serif text-3xl font-medium italic text-leaf md:text-4xl">{title}</h3>
          <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-leaf/50">{sub}</span>
        </div>
      </Reveal>
      {items.map((drink, i) => {
        const index = offset + i;
        return (
          <Reveal key={drink.id} delay={i * 0.05}>
            <button
              data-testid={`drink-row-${drink.id}`}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
              className="group block w-full border-t border-leaf/15 py-7 text-left"
            >
              <div className="flex items-baseline gap-5">
                <span className="text-xs font-medium tracking-[0.2em] text-leaf/50">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h4
                  className="font-serif text-3xl font-medium tracking-tight transition-[transform,color] duration-500 group-hover:translate-x-2 md:text-4xl"
                  style={{ color: active === index ? drink.color : "#1A241A" }}
                >
                  {drink.name}
                </h4>
                <span className="ml-auto hidden whitespace-nowrap font-cursive text-xl md:block" style={{ color: drink.color }}>
                  {drink.tagline}
                </span>
              </div>
              <p className="mt-3 pl-10 text-sm font-light leading-relaxed text-leaf/75 md:max-w-md">
                {drink.benefit}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2 pl-10">
                <span
                  data-testid={`drink-price-${drink.id}`}
                  className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream"
                  style={{ backgroundColor: drink.color }}
                >
                  {drink.price}
                </span>
                {drink.ingredients.map((ing) => (
                  <span
                    key={ing}
                    className="rounded-full border border-leaf/20 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-leaf/60 transition-colors duration-300 group-hover:border-leaf/40"
                  >
                    {ing}
                  </span>
                ))}
              </div>
            </button>
          </Reveal>
        );
      })}
    </div>
  );

  return (
    <section id="menu" className="py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <Reveal>
          <SectionBadge>01 — The Menu</SectionBadge>
        </Reveal>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.1}>
            <h2 className="max-w-xl font-serif text-5xl font-medium leading-[0.95] tracking-tighter text-ink md:text-7xl">
              Sips &amp; <span className="italic text-melon">shots</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-base font-light leading-relaxed text-leaf/80">
              Twelve blends pressed fresh every day — juices in 8oz &amp; 12oz
              bottles, shots in 2oz pours. Hover a name to see what's pouring.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-2">
          <div>
            <div className="mb-10 overflow-hidden rounded-t-[999px] rounded-b-[24px] border border-leaf/15 lg:hidden">
              <img
                src={ALL[active].image}
                alt={ALL[active].name}
                className="aspect-[4/3] w-full object-cover"
              />
            </div>

            {renderGroup("Fresh Juices", "8oz & 12oz bottles", JUICES, 0)}
            {renderGroup("Wellness Shots", "2oz pours · $4 each", SHOTS, JUICES.length)}
          </div>

          <div className="relative hidden lg:block">
            <div className="sticky top-28 h-[72vh] overflow-hidden rounded-t-[999px] rounded-b-[32px] border border-leaf/15">
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={active}
                  src={ALL[active].image}
                  alt={ALL[active].name}
                  data-testid="drink-preview-image"
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-cream/85 px-6 py-2 backdrop-blur-md">
                <span className="whitespace-nowrap font-cursive text-2xl" style={{ color: ALL[active].color }}>
                  {ALL[active].name}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="menu-order-notes"
            className="mt-16 grid grid-cols-1 gap-8 rounded-[32px] border border-leaf/15 bg-bone p-8 sm:grid-cols-2 md:p-10 lg:grid-cols-4"
          >
            {NOTES.map((n, i) => (
              <div key={n.title} data-testid={`menu-note-${i}`} className="flex items-start gap-4">
                <n.icon className="mt-1 h-6 w-6 shrink-0 text-melon" strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-xl font-medium text-ink">{n.title}</p>
                  <p className="mt-1 text-sm font-light leading-relaxed text-leaf/70">{n.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
